import React, { useState } from 'react';
import { Draggable } from "react-beautiful-dnd";
import { Paper, IconButton, Modal, Box, Button, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import './Note.css';
import axios from '../../hooks/fetchWithAuth';
import ConfirmationDialog from '../Confirmation';
import config from '../../config';
import Alerts2 from '../Alert2';

const colorOptions = [
  "#f6e1ff", "#fff9c4", "#c8e6c9", "#b3e5fc", "#ffe0b2", "#ffcdd2", "#e1bee7"
];

function getContrastYIQ(hexcolor) {
  if (!hexcolor) return "#222";
  hexcolor = hexcolor.replace("#", "");
  if (hexcolor.length === 3) {
    hexcolor = hexcolor[0] + hexcolor[0] + hexcolor[1] + hexcolor[1] + hexcolor[2] + hexcolor[2];
  }
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? "#222" : "#fff";
}

const CONTENT_CHAR_LIMIT = 200;

function stripHtml(html) {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function parseColorWithAlpha(color, alpha = 0.8) {
  if (!color) return `rgba(235,236,240,${alpha})`; // fallback claro
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    if (hex.length === 3) hex = hex.split("").map(c => c + c).join("");
    const num = parseInt(hex, 16);
    const r = (num >> 16) & 0xff;
    const g = (num >> 8) & 0xff;
    const b = num & 0xff;
    return `rgba(${r},${g},${b},${alpha})`;
  }
  // se já for rgb ou rgba, só força alfa
  if (color.startsWith("rgb")) {
    const match = color.match(/\d+/g);
    if (!match || match.length < 3) return `rgba(235,236,240,${alpha})`;
    return `rgba(${match[0]},${match[1]},${match[2]},${alpha})`;
  }
  return color; // fallback: retorna a cor como veio
}



function Note({ note, index, openNoteModal, onUpdateNote, backgroundColor, isDarkMode, onDeleteNote, fetchNotes }) {
  const backendIp = config.backend_ip;
  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [content, setContent] = useState(note.content || '');
  const [title, setTitle] = useState(note.title || '');
  const [color, setColor] = useState(note.color || "#009373");
  const [deleteNoteDialogOpen, setDeleteNoteDialogOpen] = useState(false);
  const [alert2, setAlert2] = React.useState(null);

  const isDark = !!isDarkMode;
  const modalBg = isDark ? "#2a2a2a" : "#fff";
  const modalText = isDark ? "#fff" : "#172B4D";
  const modalBorder = isDark ? "#444" : "#eee";
  const modalShadow = isDark
    ? "0 2px 24px rgba(0,0,0,0.85)"
    : "0 2px 24px rgba(0,0,0,0.08)";
  const modalHeaderBg = modalBg;
  const modalHeaderText = getContrastYIQ(modalBg);

  // Define a cor da dobra da nota SEM transparência, sempre igual à cor da lista
  const foldColor = backgroundColor && backgroundColor.trim()
    ? backgroundColor
    : (isDark ? "#2a2a2a" : "#d9d9d9");
    

  const handleSave = () => {
    setModalOpen(false);
    if (onUpdateNote) {
      onUpdateNote(note.note_id, { title, content, color });
    }
  };

  const handleOpenModal = (e) => {
    e.stopPropagation();
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setTitle(note.title || '');
    setContent(note.content || '');
    setColor(note.color || "#009373");
  };

  const handleOpenDeleteNoteDialog = () => {
    setDeleteNoteDialogOpen(true);
  };

  const handleCloseDeleteNoteDialog = () => {
    setDeleteNoteDialogOpen(false);
  };

  const showAlert = (message, severity = 'success') => {
    setAlert2({ message, severity });
    setTimeout(() => {
      setAlert2(null); // limpa o alert após 3 segundos
    }, 3000);
  };

  const handleConfirmDeleteNote = async () => {
    try {
      await axios.put(`${backendIp}/api/board/ti/delete_note`, { note_id: note.note_id });
      setDeleteNoteDialogOpen(false);
      setModalOpen(false);
      if (showAlert) showAlert('Nota excluída com sucesso!', 'success'); // dispara o alert
      if (onDeleteNote) onDeleteNote(note.note_id);
      fetchNotes();
    } catch (error) {
      if (showAlert) showAlert('Erro ao excluir nota', 'error');
      setDeleteNoteDialogOpen(false);
      fetchNotes();
    }
  };

  // Handler para bloquear digitação extra no Quill
  const handleQuillChange = (value, delta, source, editor) => {
    const plain = stripHtml(value);
    if (plain.length <= CONTENT_CHAR_LIMIT) {
      setContent(value);
    } else {
      // Impede colar texto maior que o limite
      const current = editor.getText().slice(0, CONTENT_CHAR_LIMIT);
      editor.setText(current);
      setContent(editor.root.innerHTML);
    }
  };

  return (
    <Draggable draggableId={`note-${note.note_id}`} index={index}>
      {(provided, snapshot) => (
        <div
          style={{ marginBottom: 10, cursor: 'grab', position: 'relative' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            {...provided.dragHandleProps}
            {...provided.draggableProps}
            ref={provided.innerRef}
            style={{
              backgroundColor: note.color || "#f8f8f8ff",
              borderRadius: '12px 16px 12px 12px',
              padding: '16px',
              marginBottom: '12px',
              position: 'relative',
              color: getContrastYIQ(note.color || "#eac7f9"),
              boxShadow: 'none !important',
              fontFamily: 'sans-serif',
              cursor: 'grab',
              ...provided.draggableProps.style,
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 32,
                height: 32,
                background: note.color || "#eac7f9",
                borderTopRightRadius: '16px',
                zIndex: 1,
                pointerEvents: 'none',
              }}
            />
            <div
              className='note-fold'
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 0,
                height: 0,
                borderLeft: '30px solid transparent',
                borderTop: `30px solid ${foldColor}`,
                borderTopRightRadius: '12px',
                zIndex: 2,
                boxShadow: '-6px 6px 8px -4px rgba(0,0,0,0.18)',
                pointerEvents: 'none',
              }}
            />
            {hovered && (
              <IconButton
                size="small"
                style={{
                  position: 'absolute',
                  bottom: 4,
                  right: 36,
                  zIndex: 3,
                  background: 'transparent',
                  color: getContrastYIQ(note.color || "#009373"),
                }}
                disableRipple
                disableFocusRipple
                onClick={handleOpenModal}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {hovered && (
              <IconButton
                size="small"
                style={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  zIndex: 3,
                  background: 'transparent',
                  color: "#b52626",
                }}
                disableRipple
                disableFocusRipple
                onClick={handleOpenDeleteNoteDialog}
              >
                <DeleteRoundedIcon fontSize="small" />
              </IconButton>
            )}

            <div>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  marginBottom: 4,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-line',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                }}
              >
                {note.title}
              </div>
              <div
                style={{ fontSize: 14, fontStyle: 'italic', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: note.content }}
              />
            </div>
          </div>

          <Modal
            open={modalOpen}
            onClose={handleCloseModal}
            aria-labelledby="modal-editar-nota"
            aria-describedby="modal-editar-nota-desc"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1300
            }}
          >
            <Box
              sx={{
                bgcolor: modalBg,
                color: modalText,
                borderRadius: '8px',
                boxShadow: modalShadow,
                p: 0,
                width: '100%',
                maxWidth: 420,
                minWidth: 320,
                minHeight: 340,
                outline: 'none',
                position: 'relative',
                overflow: 'hidden',
                border: `1px solid ${modalBorder}`,
                transition: 'background 0.2s, color 0.2s',
                mx: 'auto',
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderBottom: `1px solid ${modalBorder}`,
                background: modalHeaderBg,
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                transition: 'background 0.2s',
              }}>
                <Typography variant="h6" sx={{ color: modalHeaderText, fontWeight: 700 }}>
                  Editar Nota
                </Typography>
                <IconButton onClick={handleCloseModal} size="small" sx={{ color: modalHeaderText, background: 'transparent' }}>
                  <CloseIcon/>
                </IconButton>
              </Box>
              <Box sx={{
                p: 3,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                background: modalBg,
                color: modalText,
                minHeight: 220,
                transition: 'background 0.2s, color 0.2s',
                width: '100%',
                maxWidth: 380,
                mx: 'auto',
              }}>
                <label style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: modalText,
                  letterSpacing: 0.2
                }}>
                  Título
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value.slice(0, 20))}
                  maxLength={20}
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    padding: '10px 12px',
                    borderRadius: 6,
                    border: `1.5px solid ${isDark ? "#555" : "#ccc"}`,
                    background: isDark ? "#333" : "#fff",
                    color: modalText,
                    fontSize: 15,
                    marginBottom: 4,
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    boxSizing: 'border-box',
                    display: 'block',
                  }}
                  onFocus={e => e.target.style.borderColor = "#009373"}
                  onBlur={e => e.target.style.borderColor = isDark ? "#555" : "#ccc"}
                  placeholder="Digite o título da nota"
                />
                <div style={{
                  textAlign: 'right',
                  fontSize: 12,
                  color: title.length >= 20 ? '#ff6961' : modalText,
                  marginTop: 2,
                  marginBottom: 12
                }}>
                  {title.length}/20
                </div>
                <label style={{
                  fontSize: 15,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: modalText,
                  letterSpacing: 0.2
                }}>
                  Conteúdo
                </label>
                <div style={{ marginBottom: 8 }}>
                  <ReactQuill
                    value={content}
                    onChange={handleQuillChange}
                    modules={{
                      toolbar: [
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    formats={[
                      'bold', 'italic', 'underline', 'strike',
                      'list', 'bullet', 'link'
                    ]}
                    style={{
                      background: isDark ? "#333" : "#fff",
                      color: modalText,
                      borderRadius: 6,
                      minHeight: 80,
                      maxHeight: 120,
                      marginBottom: 0,
                      overflowY: 'auto'
                    }}
                    theme="snow"
                    onKeyDown={e => {
                      const plain = stripHtml(content);
                      // Permite teclas de navegação, delete, backspace, etc
                      if (
                        plain.length >= CONTENT_CHAR_LIMIT &&
                        e.key.length === 1 && // só bloqueia caracteres imprimíveis
                        !e.ctrlKey && !e.metaKey && !e.altKey
                      ) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <div style={{
                    textAlign: 'right',
                    fontSize: 12,
                    color: stripHtml(content).length >= CONTENT_CHAR_LIMIT ? '#ff6961' : modalText,
                    marginTop: 2,
                    marginBottom: 12
                  }}>
                    {stripHtml(content).length}/{CONTENT_CHAR_LIMIT}
                  </div>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 14, color: modalText }}>Cor:</span>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {colorOptions.map(opt => (
                      <div
                        key={opt}
                        onClick={() => setColor(opt)}
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          background: opt,
                          border: color === opt ? '2px solid #4b2e83' : '2px solid #fff',
                          cursor: 'pointer',
                          boxShadow: color === opt ? '0 0 4px #4b2e83' : undefined,
                          outline: isDark && color === opt ? '1px solid #fff' : undefined,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  fullWidth
                  sx={{
                    mt: 2,
                    fontWeight: 600,
                    borderRadius: 2,
                    background: "#009373",
                    color: "#fff",
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                      background: "#009373",
                      opacity: 0.9,
                    }
                  }}
                  disabled={stripHtml(content).length > CONTENT_CHAR_LIMIT}
                >
                  Salvar
                </Button>
              </Box>
            </Box>
          </Modal>
          <ConfirmationDialog
            open={deleteNoteDialogOpen}
            onClose={handleCloseDeleteNoteDialog}
            onConfirm={handleConfirmDeleteNote}
            title="Confirmar Exclusão"
            message="Tem certeza que deseja excluir esta nota? Esta ação não pode ser desfeita."
          />
          {alert2 && <Alerts2 message={alert2.message} severity={alert2.severity} />}
        </div>
      )}
    </Draggable>
  );
}

export default Note;
